import { ID_IMAGE_URL_TTL_SECONDS, type GetVerificationImagesResponse } from "@wallyo/contracts/console/verifications";
import type { ConsoleClient } from "../../api/client";
import { getVerificationImages } from "../../api/console-api";

/**
 * The ID images of one verification. They are the most sensitive thing the
 * console shows, so:
 *
 * - nothing is asked for until the reviewer says "Afficher les documents"
 *   (each ask is audited by the server, with the reviewer's name);
 * - without VIEW_ID_IMAGES nothing is ever asked for;
 * - the signed URLs live only in this object's memory: never in the hash,
 *   history, storage or a link. "Masquer", leaving the verification, or the
 *   URLs' expiry drops them;
 * - a late answer for a request that was dropped is thrown away.
 */
export type ImagesState =
  | { status: "locked" }
  | { status: "hidden"; error: unknown }
  | { status: "loading" }
  | { status: "shown"; images: GetVerificationImagesResponse["images"]; expiresAt: string }
  | { status: "expired" };

export type ImageViewer = {
  get(): ImagesState;
  subscribe(listener: () => void): () => void;
  /** The reviewer asked to see the documents. */
  show(): Promise<void>;
  /** "Masquer": the URLs are dropped, not hidden. */
  hide(): void;
  /** Leaving: the same as hide, and any answer still on its way is ignored. */
  dispose(): void;
};

export type ImageViewerOptions = {
  client: ConsoleClient;
  identityVerificationId: string;
  canView: boolean;
  setTimer?: (run: () => void, ms: number) => unknown;
  clearTimer?: (timer: unknown) => void;
};

const HIDDEN: ImagesState = { status: "hidden", error: null };

export function createImageViewer(options: ImageViewerOptions): ImageViewer {
  const setTimer = options.setTimer ?? ((run, ms) => setTimeout(run, ms));
  const clearTimer = options.clearTimer ?? ((timer) => clearTimeout(timer as ReturnType<typeof setTimeout>));
  const listeners = new Set<() => void>();
  let state: ImagesState = options.canView ? HIDDEN : { status: "locked" };
  let generation = 0;
  let timer: unknown = null;

  const set = (next: ImagesState) => {
    state = next;
    listeners.forEach((listener) => listener());
  };
  const stopTimer = () => {
    if (timer !== null) clearTimer(timer);
    timer = null;
  };
  const drop = (next: ImagesState) => {
    generation += 1;
    stopTimer();
    if (options.canView) set(next);
  };

  return {
    get: () => state,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    async show() {
      if (!options.canView || state.status === "loading" || state.status === "shown") return;
      const mine = ++generation;
      set({ status: "loading" });
      try {
        const answer = await getVerificationImages(options.client, options.identityVerificationId);
        if (mine !== generation) return;
        set({ status: "shown", images: answer.images, expiresAt: answer.expiresAt });
        // Counted from when they arrived: the reviewer's clock may differ from the server's.
        timer = setTimer(() => {
          timer = null;
          if (mine === generation) drop({ status: "expired" });
        }, ID_IMAGE_URL_TTL_SECONDS * 1000);
      } catch (error) {
        if (mine === generation) set({ status: "hidden", error });
      }
    },
    hide: () => drop(HIDDEN),
    dispose: () => drop(HIDDEN),
  };
}
